import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  MessageSquare, Users, Gauge, ChevronRight, FolderKanban, Plus, Calendar, Download,
  AlertTriangle, Reply,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/feature/kpi-card";
import { ChartVolume } from "@/components/feature/chart-volume";
import { ChartDonut } from "@/components/feature/chart-donut";
import { SocialDot } from "@/components/feature/social-dot";
import { Progress } from "@/components/feature/progress";
import { Sparkline } from "@/components/feature/sparkline";
import { useDashboardSummary, useUnreadMentions, useUsage } from "@/hooks/use-dashboard";
import { useProjects } from "@/hooks/use-projects";
import { useActiveProject } from "@/hooks/use-active-project";
import { useMe } from "@/hooks/use-me";
import { formatNumber, formatPercent } from "@/lib/utils";

export const Route = createFileRoute("/_auth/dashboard")({
  component: DashboardPage,
});

const NETWORK_LABEL: Record<string, string> = {
  x: "X (Twitter)", ig: "Instagram", fb: "Facebook", li: "LinkedIn",
  yt: "YouTube", tt: "TikTok", news: "Noticias", reddit: "Reddit",
};

const PROJECT_COLORS = ["brand", "good", "warn", "purple"] as const;

const FOLDER_BG: Record<string, string> = {
  brand: "bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300",
  good: "bg-emerald-50 dark:bg-emerald-900/30 text-good",
  warn: "bg-orange-50 dark:bg-orange-900/30 text-warn",
  purple: "bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300",
};

function DashboardPage() {
  const [range, setRange] = useState<7 | 30 | 90>(7);
  const { data: me } = useMe();
  const { data: summary, isLoading } = useDashboardSummary(range);
  const { data: unread } = useUnreadMentions(5);
  const { data: usage } = useUsage();
  const { data: projects } = useProjects({ limit: 3 });
  const { activeId } = useActiveProject();
  const { data: allProjects } = useProjects({ limit: 200 });
  const activeProject = allProjects?.find((p) => p.id === activeId) ?? null;

  // Sparkline data del daily count
  const spark = (summary?.daily ?? []).map((d) => d.count);
  // Sparkline reverse para alcance
  const sparkRev = [...spark].reverse();

  // Top sources con porcentajes
  const totalSources = (summary?.top_sources ?? []).reduce((a, s) => a + s.count, 0) || 1;

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs text-ink-500 mb-1">
            Inicio · {activeProject?.name ?? me?.account_name ?? "—"}
          </p>
          <h1 className="text-2xl font-bold text-ink-900 dark:text-white">
            Buen día, {me?.name?.split(" ")[0] ?? "—"}
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Esto es lo que está pasando con tus proyectos hoy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Calendar size={14} /> Últimos {range} días
          </Button>
          <Button variant="outline" size="sm">
            <Download size={14} /> Exportar
          </Button>
          <Link to="/monitor">
            <Button size="sm">
              <Plus size={14} /> Nuevo proyecto
            </Button>
          </Link>
        </div>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {isLoading || !summary ? (
          <>
            <Skeleton className="h-32" /><Skeleton className="h-32" />
            <Skeleton className="h-32" /><Skeleton className="h-32" />
          </>
        ) : (
          <>
            <KpiCard
              label={`Menciones últ. ${summary.range_days}d`}
              value={formatNumber(summary.total_mentions)}
              delta={formatPercent(summary.delta_pct)}
              tone={(summary.delta_pct ?? 0) >= 0 ? "good" : "bad"}
              Icon={MessageSquare}
              iconTone="muted"
              spark={spark}
            />
            <SentimentKpi summary={summary} />
            <KpiCard
              label="Alcance estimado"
              value={formatNumber(summary.reach)}
              sub={`impresiones · últ. ${summary.range_days}d`}
              Icon={Users}
              iconTone="muted"
              spark={sparkRev}
            />
            <ConsumptionKpi
              consumed={usage?.mentions.consumed ?? 0}
              quota={usage?.mentions.quota ?? 1}
              percent={usage?.mentions.percent ?? 0}
            />
          </>
        )}
      </div>

      {/* Volume + sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-ink-900 dark:text-white">Volumen de menciones</h3>
              <p className="text-xs text-ink-500">Últimos {range} días — todos los proyectos</p>
            </div>
            <div className="flex items-center gap-1 text-xs">
              {([7, 30, 90] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setRange(p)}
                  className={`h-7 px-2.5 rounded-md ${
                    range === p
                      ? "bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
                      : "text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-800"
                  }`}
                >
                  {p}d
                </button>
              ))}
            </div>
          </div>
          {summary ? <ChartVolume data={summary.daily} /> : <Skeleton className="h-60" />}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-ink-900 dark:text-white">Top fuentes</h3>
            <Link to="/monitor" className="text-xs text-brand-600 hover:text-brand-700">
              Ver todo
            </Link>
          </div>
          <div className="space-y-3">
            {!summary || summary.top_sources.length === 0 ? (
              <p className="text-xs text-ink-500">Sin datos aún.</p>
            ) : (
              summary.top_sources.slice(0, 5).map((s) => {
                const pct = Math.round((s.count / totalSources) * 100);
                return (
                  <div key={s.network} className="flex items-center gap-3">
                    <SocialDot network={s.network} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-ink-800 dark:text-ink-100 font-medium">
                          {NETWORK_LABEL[s.network] ?? s.network}
                        </span>
                        <span className="text-ink-500">{formatNumber(s.count)}</span>
                      </div>
                      <Progress value={pct} className="mt-1.5" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>

      {/* Unread + recent projects */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 !p-0 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 dark:border-ink-800">
            <div>
              <h3 className="font-semibold text-ink-900 dark:text-white">Últimas menciones no leídas</h3>
              <p className="text-xs text-ink-500">{unread?.length ?? 0} esperan tu respuesta</p>
            </div>
            <Link
              to="/responder"
              className="text-xs text-brand-600 hover:text-brand-700 font-medium"
            >
              Abrir bandeja →
            </Link>
          </div>
          <ul className="divide-y divide-ink-100 dark:divide-ink-800">
            {!unread ? (
              <li className="px-5 py-4">
                <Skeleton className="h-16" />
              </li>
            ) : unread.length === 0 ? (
              <li className="px-5 py-6 text-center text-sm text-ink-500">
                Sin menciones aún. Cuando los workers capturen, aparecerán aquí.
              </li>
            ) : (
              unread.map((m) => <UnreadRow key={m.id} m={m} />)
            )}
          </ul>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-ink-900 dark:text-white">Proyectos recientes</h3>
            <Link to="/monitor" className="text-xs text-brand-600 hover:text-brand-700 font-medium">
              Todos →
            </Link>
          </div>
          <div className="space-y-2">
            {!projects ? (
              <Skeleton className="h-24" />
            ) : projects.length === 0 ? (
              <p className="text-xs text-ink-500">Crea tu primer proyecto desde Monitorización.</p>
            ) : (
              projects.slice(0, 3).map((p, i) => {
                const color = PROJECT_COLORS[i % PROJECT_COLORS.length];
                return (
                  <Link
                    key={p.id}
                    to="/monitor/$projectId"
                    params={{ projectId: String(p.id) }}
                    className="w-full text-left p-3 rounded-lg hover:bg-ink-50 dark:hover:bg-ink-800 transition-colors flex items-center gap-3 ring-1 ring-transparent hover:ring-ink-100 dark:hover:ring-ink-700"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${FOLDER_BG[color]}`}>
                      <FolderKanban size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink-900 dark:text-white truncate">{p.name}</div>
                      <div className="text-[11px] text-ink-500 flex items-center gap-2">
                        <span>{formatNumber(p.mentions_count)} menciones</span>
                        {p.last_mention_at && (
                          <>
                            <span>·</span>
                            <span>últ. {new Date(p.last_mention_at).toLocaleDateString("es-PE")}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-ink-400" />
                  </Link>
                );
              })
            )}
          </div>

          {summary && summary.delta_pct != null && summary.delta_pct > 50 && (
            <div className="mt-4 rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-3 flex items-start gap-3">
              <AlertTriangle size={16} className="text-warn mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-orange-800 dark:text-orange-200">
                  Pico de menciones detectado
                </p>
                <p className="text-[11px] text-orange-700 dark:text-orange-300 mt-0.5">
                  +{summary.delta_pct.toFixed(0)}% vs período anterior. Revisa lo que está pasando.
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function SentimentKpi({ summary }: { summary: { sentiment: { pos: number; neg: number; neu: number; nss: number } } }) {
  const { pos, neg, neu, nss } = summary.sentiment;
  const total = pos + neg + neu || 1;
  const posPct = Math.round((pos / total) * 100);
  const neuPct = Math.round((neu / total) * 100);
  const negPct = Math.round((neg / total) * 100);
  const isHealthy = nss >= 0;
  return (
    <Card>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs text-ink-500">Sentimiento neto</p>
          <h2 className="text-2xl font-bold text-ink-900 dark:text-white mt-1">
            {nss >= 0 ? "+" : ""}
            {nss}
          </h2>
          <p className={`text-[11px] mt-1 ${isHealthy ? "text-good" : "text-bad"}`}>
            {isHealthy ? "▲ saludable" : "▼ atención"}
          </p>
        </div>
        <ChartDonut pos={pos} neg={neg} neu={neu} />
      </div>
      <div className="flex items-center gap-3 text-[11px]">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-good" /> Pos {posPct}%
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-ink-400" /> Neu {neuPct}%
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-bad" /> Neg {negPct}%
        </span>
      </div>
    </Card>
  );
}

function ConsumptionKpi({ consumed, quota, percent }: { consumed: number; quota: number; percent: number }) {
  const tone: "good" | "warn" | "bad" = percent >= 90 ? "bad" : percent >= 70 ? "warn" : "good";
  const iconBg: Record<typeof tone, string> = {
    good: "bg-emerald-50 dark:bg-emerald-900/30 text-good",
    warn: "bg-orange-50 dark:bg-orange-900/30 text-warn",
    bad: "bg-red-50 dark:bg-red-900/30 text-bad",
  };
  const barColor: Record<typeof tone, string> = {
    good: "bg-good",
    warn: "bg-warn",
    bad: "bg-bad",
  };
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-ink-500">Consumo actual</p>
          <div className="mt-1 flex items-baseline gap-2">
            <h2 className="text-2xl font-bold text-ink-900 dark:text-white">
              {percent.toFixed(0)}%
            </h2>
            <span className="text-xs text-ink-500">de {formatNumber(quota)}</span>
          </div>
          <p className="text-[11px] text-ink-500 mt-1">
            {formatNumber(consumed)} / {formatNumber(quota)} menciones
          </p>
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconBg[tone]}`}>
          <Gauge size={16} />
        </div>
      </div>
      <Progress value={percent} className="mt-3" barClassName={barColor[tone]} />
    </Card>
  );
}

function UnreadRow({ m }: { m: { id: number; network: string; author: string | null; text: string; sentiment: string | null; published_at: string } }) {
  const tone = m.sentiment === "pos" ? "good" : m.sentiment === "neg" ? "bad" : "muted";
  const label = m.sentiment === "pos" ? "Positivo" : m.sentiment === "neg" ? "Negativo" : "Neutral";
  const initials = (m.author ?? "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <li className="px-5 py-3.5 flex items-start gap-3 hover:bg-ink-50 dark:hover:bg-ink-800/50 transition-colors">
      <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 flex items-center justify-center text-xs font-semibold shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-ink-900 dark:text-white">{m.author ?? "Anónimo"}</span>
          <SocialDot network={m.network} size={14} />
          <span className="text-xs text-ink-400">·</span>
          <span className="text-xs text-ink-400">
            {new Date(m.published_at).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" })}
          </span>
          <Badge variant={tone}>{label}</Badge>
        </div>
        <p className="text-sm text-ink-700 dark:text-ink-200 mt-1 line-clamp-2">{m.text}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Link to="/responder">
          <Button variant="outline" size="sm">
            <Reply size={12} /> Responder
          </Button>
        </Link>
      </div>
    </li>
  );
}
